import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  LayoutFrame,
  ListTemplate,
  PluginListItem,
  SearchBar,
  SearchFilterBar,
  StatsRow,
  formatStatusLabel,
  getStatusVariant,
  registerHomeTile,
} from '@room/shared-ui';
import { useToast } from '@room/shared-ui/components/UI';
import { Badge } from '@room/shared-ui/components/ui/badge';
import { Button } from '@room/shared-ui/components/ui/button';
import { Card } from '@room/shared-ui/components/ui/card';
import { marketplaceAPI, pipelineAPI } from '../../../lib/api';
import { fetchPluginCatalog } from '../../../lib/pluginCatalog';
import WorkflowVisualization from '../../../components/WorkflowVisualization';
import styles from './AdminPipeline.module.css';
import { adminTileVisible } from '../homeTiles';

type PipelineItem = {
  id: string;
  name?: string;
  version?: string;
  author?: string;
  description?: string;
  kind: string;
  type?: string;
  status: string;
  enabled?: boolean;
  updatedAt?: number | null;
};

registerHomeTile({
  id: 'home-admin-pipeline',
  label: 'Pipeline',
  icon: '📈',
  route: '/admin/pipeline',
  section: 'main',
  order: 70,
  visible: adminTileVisible,
});

export default function AdminPipeline() {
  const queryClient = useQueryClient();
  const [actionId, setActionId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<PipelineItem | null>(null);
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionWarnings, setActionWarnings] = useState<string[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [enabledFilter, setEnabledFilter] = useState('all');
  const [showControls, setShowControls] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 900;
  });
  const { addToast } = useToast();

  const {
    data: catalog,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['plugin-catalog'],
    queryFn: fetchPluginCatalog,
    refetchInterval: 30000,
  });

  const errorMessage = error instanceof Error ? error.message : null;

  const { data: detailResponse } = useQuery({
    queryKey: ['pipeline-detail', selectedItem?.id],
    queryFn: () => (selectedItem ? pipelineAPI.getDetail(selectedItem.id) : Promise.resolve(null)),
    enabled: Boolean(selectedItem),
  });

  const detailSteps = detailResponse?.data?.steps ?? detailResponse?.steps ?? [];
  const detailDefinition = detailResponse?.data?.stepDefinitions ?? detailResponse?.stepDefinitions ?? [];
  const detailReview = detailResponse?.data?.review ?? detailResponse?.review ?? null;
  const storePublishStep = detailSteps.find((step: { stepId?: string }) => step.stepId === 'store_publish');
  const storePublishStatus = storePublishStep?.status ?? 'not_started';
  const isPublished = storePublishStatus === 'complete';

  const items: PipelineItem[] = (catalog?.items ?? [])
    .filter((item) => item.inPipeline)
    .map((item) => ({
      id: item.id,
      name: item.name,
      version: item.version,
      author: item.author,
      description: item.description,
      kind: item.kind ?? 'plugin',
      type: item.type,
      status: item.pipelineStatus ?? item.status ?? 'unknown',
      enabled: item.enabled,
      updatedAt: item.updatedAt ?? null,
    }));
  const { kinds, statuses } = useMemo(() => {
    const kindSet = new Set<string>();
    const statusSet = new Set<string>();
    items.forEach((item) => {
      if (item.kind) kindSet.add(item.kind);
      if (item.status) statusSet.add(item.status);
    });
    return {
      kinds: Array.from(kindSet).sort(),
      statuses: Array.from(statusSet).sort(),
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items
      .filter((item) => {
        if (statusFilter !== 'all' && item.status !== statusFilter) {
          return false;
        }
        if (kindFilter !== 'all' && item.kind !== kindFilter) {
          return false;
        }
        if (enabledFilter !== 'all') {
          const enabledValue = enabledFilter === 'enabled';
          if (Boolean(item.enabled) !== enabledValue) {
            return false;
          }
        }
        if (!query) return true;
        const haystack = [
          item.name,
          item.id,
          item.description,
          item.author,
          item.kind,
          item.status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  }, [items, search, statusFilter, kindFilter, enabledFilter]);

  const filterSummary = useMemo(() => {
    const summaryParts: string[] = [];
    if (statusFilter !== 'all') summaryParts.push(`Status: ${statusFilter}`);
    if (kindFilter !== 'all') summaryParts.push(`Kind: ${kindFilter}`);
    if (enabledFilter !== 'all') summaryParts.push(`Enabled: ${enabledFilter}`);
    return summaryParts;
  }, [statusFilter, kindFilter, enabledFilter]);

  const hasActiveFilters = filterSummary.length > 0;

  const handleSelectItem = (item: PipelineItem) => {
    setSelectedItem(item);
    setNote('');
    setActionError(null);
    setActionWarnings(null);
  };

  const actionMutation = useMutation({
    mutationFn: async (payload: { action: 'approve' | 'reject' | 'changes' | 'unpublish'; id: string }) => {
      if (payload.action === 'approve') {
        return pipelineAPI.approve(payload.id, { note });
      }
      if (payload.action === 'reject') {
        return pipelineAPI.reject(payload.id, { note });
      }
      if (payload.action === 'unpublish') {
        return marketplaceAPI.unpublish(payload.id);
      }
      return pipelineAPI.requestChanges(payload.id, { note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-detail'] });
    },
  });

  const handleAction = async (action: 'approve' | 'reject' | 'changes' | 'unpublish', id: string) => {
    setActionId(id);
    try {
      const result = await actionMutation.mutateAsync({ action, id });
      setNote('');
      setActionError(null);
      if (action === 'approve') {
        const warnings = (result as { warnings?: string[] })?.warnings ?? null;
        setActionWarnings(warnings && warnings.length > 0 ? warnings : null);
      } else {
        setActionWarnings(null);
      }
      if (action === 'approve' && (result as { publishStatus?: string })?.publishStatus === 'published') {
        addToast('Approved and published to the Store.', 'success');
      } else if (action === 'unpublish') {
        addToast('Plugin unpublished from the Store.', 'success');
      } else {
        addToast(`Pipeline item ${action}d.`, 'success');
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to update pipeline item.';
      setActionError(message);
      setActionWarnings(null);
      addToast(message, 'error');
    } finally {
      setActionId(null);
    }
  };

  return (
    <LayoutFrame withSection={false} flush className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitleRow}>
          <h2 className={styles.pageTitle}>Pipeline Monitor</h2>
          {isFetching && !isLoading ? <span className={styles.refreshBadge}>Refreshing…</span> : null}
        </div>
        <p className={`page-subtitle ${styles.pageSubtitle} content-wrap`}>
          Monitor plugin build, QA, and deployment pipelines.
        </p>
        <button
          type="button"
          className={styles.controlsToggle}
          onClick={() => setShowControls((prev) => !prev)}
        >
          {showControls ? 'Hide filters & stats' : 'Show filters & stats'}
        </button>
      </div>

      {showControls && (
        <>
          {catalog?.pipelineCounts && (
            <StatsRow
              className={styles.statsRow}
              items={[
                { label: 'Total items', value: catalog.pipelineCounts.total, tone: 'primary' },
                { label: 'Enabled', value: catalog.pipelineCounts.enabled, tone: 'accent' },
                { label: 'Review queue', value: catalog.pipelineCounts.review },
                { label: 'Installed', value: catalog.pipelineCounts.installed },
              ]}
            />
          )}

          <section className={styles.controls}>
            <SearchFilterBar
              search={(
                <SearchBar
                  label="Search"
                  className={styles.searchInput}
                  placeholder="Search by name, id, author..."
                  value={search}
                  onSearch={setSearch}
                  debounce={300}
                  fullWidth
                />
              )}
              filters={(
                <div className={styles.filterRow}>
                  <div className="input-wrapper">
                    <label className="input-label">Status</label>
                    <select
                      className={`input input-base ${styles.select}`}
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">All statuses</option>
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-wrapper">
                    <label className="input-label">Kind</label>
                    <select
                      className={`input input-base ${styles.select}`}
                      value={kindFilter}
                      onChange={(event) => setKindFilter(event.target.value)}
                    >
                      <option value="all">All kinds</option>
                      {kinds.map((kind) => (
                        <option key={kind} value={kind}>
                          {kind}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-wrapper">
                    <label className="input-label">Enabled</label>
                    <select
                      className={`input input-base ${styles.select}`}
                      value={enabledFilter}
                      onChange={(event) => setEnabledFilter(event.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </div>
                </div>
              )}
              meta={(
                <span className={`${styles.filterSummary} content-wrap`}>
                  {hasActiveFilters ? `Active filters: ${filterSummary.join(' · ')}` : 'No filters applied'}
                </span>
              )}
              actions={hasActiveFilters && (
                <Button
                  className="ui-button--small"
                  onClick={() => {
                    setStatusFilter('all');
                    setKindFilter('all');
                    setEnabledFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
            />
          </section>
        </>
      )}

      <section className={styles.contentGrid}>
        <Card className={styles.listCard}>
          <div className={styles.listHeader}>
            <p className={styles.listTitle}>Pipeline items</p>
            <p className={styles.listMeta}>{filteredItems.length} results</p>
          </div>

          {errorMessage && (
            <Alert variant="error" title="Unable to load pipeline">
              {errorMessage}
            </Alert>
          )}
          {!errorMessage && (
            <ListTemplate
              items={filteredItems}
              loading={isLoading}
              loadingComponent={<p className={styles.stateText}>Loading pipeline status...</p>}
              emptyState={(
                <div className={styles.emptyState}>
                  <p>No pipeline items found.</p>
                  <span>Try adjusting your filters or search terms.</span>
                </div>
              )}
              listClassName={styles.list}
              renderItem={(item) => {
                const updatedAtLabel = item.updatedAt
                  ? new Date(item.updatedAt).toLocaleString()
                  : '—';
                const statusVariant = getStatusVariant(item.status);

                return (
                  <PluginListItem
                    key={item.id}
                    className={styles.listItem}
                    active={selectedItem?.id === item.id}
                    onClick={() => handleSelectItem(item)}
                    title={item.name ?? item.id}
                    subtitle={(
                      <span className={styles.itemDescription}>
                        {item.description ?? 'No description'}
                      </span>
                    )}
                    meta={`Updated ${updatedAtLabel}`}
                    badges={(
                      <div className={styles.itemBadges}>
                        <Badge variant="secondary">{formatStatusLabel(item.kind)}</Badge>
                        <Badge variant={statusVariant}>{formatStatusLabel(item.status)}</Badge>
                      </div>
                    )}
                    actions={[
                      {
                        label: actionId === item.id ? 'Processing…' : 'Approve',
                        icon: '✅',
                        tone: 'success',
                        onClick: () => handleAction('approve', item.id),
                        disabled: actionId === item.id,
                      },
                      {
                        label: actionId === item.id ? 'Processing…' : 'Changes',
                        icon: '✏️',
                        tone: 'warning',
                        onClick: () => handleAction('changes', item.id),
                        disabled: actionId === item.id,
                      },
                      {
                        label: actionId === item.id ? 'Processing…' : 'Reject',
                        icon: '🛑',
                        tone: 'danger',
                        onClick: () => handleAction('reject', item.id),
                        disabled: actionId === item.id,
                      },
                    ]}
                  />
                );
              }}
              keyExtractor={(item) => item.id}
            />
          )}
        </Card>

        <Card className={styles.detailCard}>
          {selectedItem ? (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <p className={styles.detailTitle}>{selectedItem.name ?? selectedItem.id}</p>
                  <p className={styles.detailSubtitle}>{selectedItem.description ?? 'No description'}</p>
                  <div className={styles.detailBadges}>
                    <Badge variant={getStatusVariant(selectedItem.status)}>
                      {formatStatusLabel(selectedItem.status)}
                    </Badge>
                    {isPublished && <Badge variant="success">Published to Store</Badge>}
                  </div>
                </div>
                <div className={styles.detailHeaderMeta}>
                  <Badge variant={getStatusVariant(selectedItem.status)}>
                    {formatStatusLabel(selectedItem.status)}
                  </Badge>
                  <Badge variant={isPublished ? 'success' : 'warning'}>
                    Store: {formatStatusLabel(storePublishStatus)}
                  </Badge>
                </div>
              </div>

              <div className={styles.detailMetaGrid}>
                <div>
                  <p className={styles.metaLabel}>Kind</p>
                  <p className={styles.metaValue}>{selectedItem.kind}</p>
                </div>
                <div>
                  <p className={styles.metaLabel}>Version</p>
                  <p className={styles.metaValue}>{selectedItem.version ?? '—'}</p>
                </div>
                <div>
                  <p className={styles.metaLabel}>Author</p>
                  <p className={styles.metaValue}>{selectedItem.author ?? '—'}</p>
                </div>
                <div>
                  <p className={styles.metaLabel}>Updated</p>
                  <p className={styles.metaValue}>
                    {selectedItem.updatedAt ? new Date(selectedItem.updatedAt).toLocaleString() : '—'}
                  </p>
                </div>
              </div>

              <div className={styles.noteSection}>
                <label className={styles.noteLabel} htmlFor="review-note">
                  Review note
                </label>
                <textarea
                  id="review-note"
                  className={styles.noteInput}
                  rows={4}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Add context for approval or feedback for changes."
                />
                <p className={styles.noteHelper}>
                  Approving will publish this plugin to the Store once metadata is validated.
                </p>
              </div>

              {actionWarnings && (
                <Alert variant="warning" title="Published with warnings">
                  <ul className={styles.warningList}>
                    {actionWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {actionError && (
                <Alert variant="error" title="Action failed">
                  {actionError}
                </Alert>
              )}

              <div className={styles.detailActions}>
                <Button
                  variant="primary"
                  className="ui-button--small"
                  onClick={() => handleAction('approve', selectedItem.id)}
                  disabled={actionId === selectedItem.id}
                >
                  {actionId === selectedItem.id ? 'Processing…' : 'Approve & publish'}
                </Button>
                <Button
                  className="ui-button--small"
                  onClick={() => handleAction('changes', selectedItem.id)}
                  disabled={actionId === selectedItem.id}
                >
                  {actionId === selectedItem.id ? 'Processing…' : 'Request changes'}
                </Button>
                <Button
                  className="ui-button--small"
                  onClick={() => handleAction('reject', selectedItem.id)}
                  disabled={actionId === selectedItem.id}
                >
                  {actionId === selectedItem.id ? 'Processing…' : 'Reject'}
                </Button>
                {isPublished && (
                  <Button
                    variant="destructive"
                    className="ui-button--small"
                    onClick={() => handleAction('unpublish', selectedItem.id)}
                    disabled={actionId === selectedItem.id}
                  >
                    {actionId === selectedItem.id ? 'Processing…' : 'Unpublish'}
                  </Button>
                )}
                {isPublished && (
                  <a
                    className={styles.storeLink}
                    href={`https://room1.attyzen.com/marketplace/plugins/${selectedItem.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on Store
                  </a>
                )}
              </div>
              {actionId === selectedItem.id && (
                <p className={styles.detailActionFeedback}>Updating pipeline item…</p>
              )}

              <div className={styles.workflowSection}>
                {detailDefinition.length > 0 ? (
                  <WorkflowVisualization
                    steps={detailDefinition}
                    currentSteps={detailSteps}
                    reviewStatus={detailReview?.status ?? null}
                    reviewNote={detailReview?.note ?? null}
                    reviewer={detailReview?.actor ?? null}
                  />
                ) : detailSteps.length > 0 ? (
                  <ul className={styles.stepList}>
                    {detailSteps.map((step) => (
                      <li key={step.stepId}>
                        <strong>{step.stepId}</strong> — {step.status}
                        {step.note && <div className={styles.stepNote}>{step.note}</div>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.emptyNote}>No checklist steps found.</p>
                )}
              </div>
            </>
          ) : (
            <div className={styles.emptyDetail}>
              <p>Select a pipeline item to review details.</p>
              <span>Pipeline steps and review actions will appear here.</span>
            </div>
          )}
        </Card>
      </section>
    </LayoutFrame>
  );
}
